import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import getBaseUrl from "../getBaseUrl";

const maxRetries = 2;

const dynamicBaseQuery = async (args: any, api: any, extraOptions: any) => {
  const rawBaseQuery = fetchBaseQuery({ baseUrl: getBaseUrl() });
  return rawBaseQuery(args, api, extraOptions);
};

const baseQuery = retry(dynamicBaseQuery, { maxRetries });

export const roleAPI = createApi({
  reducerPath: "roleAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetRoles", "tagGetRole", "tagGetRolesLight"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ROLES */
    getRoles: builder.query({
      query: (body: any) => ({
        url: `/roles/?&page=${body.page}&limit=${body.limit}&sort=created_at&sort_direction=asc${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* GET ALL ROLES LIGHT */
    getRolesLight: builder.query({
      query: (body: any) => ({
        url: `/roles/light/${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRolesLight"],
    }),
    //* *********************************************************** */
    //* GET ROLES BY ORGANIZATION */
    getRolesByOrganization: builder.query({
      query: (body: any) => ({
        url: `/roles/?filter_target=organization_id&filter=${body.orgId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* GET ONE ROLE */
    getRole: builder.query({
      query: (body: any) => ({
        url: `/roles/${body.roleId}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetRole"],
    }),
    //* *********************************************************** */
    //* CREATE ROLE */
    postRole: builder.mutation({
      query: (body: any) => ({
        url: "/roles/",
        method: "post",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* EDIT ROLE */
    putRole: builder.mutation({
      query: (body: any) => ({
        url: `/roles/${body.id}`,
        method: "put",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
    //* DELETE ROLE */
    deleteRole: builder.mutation({
      query: (body: any) => ({
        url: `/roles/${body.id}`,
        method: "delete",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetRoles"],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetRolesQuery,
  useGetRolesLightQuery,
  useLazyGetRolesLightQuery,
  useLazyGetRoleQuery,
  useLazyGetRolesByOrganizationQuery,
  usePostRoleMutation,
  usePutRoleMutation,
  useDeleteRoleMutation,
} = roleAPI;
